import React, { useState } from 'react';
import { MessageCircle, Volume2, Brain, Mic, Users, ArrowRight, ArrowLeft, Calendar, Clock, User, Search, Filter } from 'lucide-react';


const SpeechTherapyBlog = () => {
  const disorders = [
    {
      title: "Articulation Disorder",
      description: "This occurs when someone has difficulty pronouncing certain sounds correctly, making their speech hard to understand. It's common in young children but can persist without intervention.",
      icon: <Volume2 className="w-8 h-8 text-blue-600" />,
      color: "bg-blue-50 border-blue-200"
    },
    {
      title: "Stammering (Stuttering)",
      description: "Characterized by disruptions in the flow of speech, such as repeating sounds, syllables, or words, or pausing involuntarily, stammering can impact confidence and communication in social situations.",
      icon: <MessageCircle className="w-8 h-8 text-purple-600" />,
      color: "bg-purple-50 border-purple-200"
    },
    {
      title: "Voice Disorder",
      description: "This involves problems with the pitch, volume, or quality of the voice. Someone with a voice disorder may sound hoarse, breathy, or lose their voice easily.",
      icon: <Mic className="w-8 h-8 text-green-600" />,
      color: "bg-green-50 border-green-200"
    },
    {
      title: "Expressive Language Disorder",
      description: "People with this disorder struggle to express themselves using words, sentences, or gestures, even though they may understand language well.",
      icon: <Brain className="w-8 h-8 text-orange-600" />,
      color: "bg-orange-50 border-orange-200"
    },
    {
      title: "Receptive Language Disorder",
      description: "This affects a person's ability to understand spoken or written language, making it hard to follow directions or comprehend what others are saying.",
      icon: <Users className="w-8 h-8 text-red-600" />,
      color: "bg-red-50 border-red-200"
    }
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="mb-12 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-[#0CC8E8] to-[#2DEEAA] mb-4">
          <MessageCircle className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0CC8E8] to-[#2DEEAA] mb-4">
          Speech Therapy Insights
        </h1>
        <p className="text-gray-500 text-lg">
          5 Key Speech and Language Disorders to Know
        </p>
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-[#222a32] rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 mb-12">
        <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-4">
          Communication is at the heart of how we connect, learn, and share our experiences with the world. But for many people, speech and language disorders can make this everyday activity a real challenge. Understanding the different types of disorders is the first step in supporting those who may be struggling.
        </p>
        <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
          In this post, we'll introduce five common speech and language disorders:
        </p>
      </div>

      {/* Disorders List */}
      <div className="grid gap-8">
        {disorders.map((disorder, index) => (
          <div
            key={index}
            className={`${disorder.color} dark:bg-[#333c44] border rounded-2xl p-6 hover:shadow-xl transition-transform transform hover:scale-105`}
          >
            <div className="flex gap-4 items-start">
              <div className="p-3 bg-white dark:bg-[#222a32] rounded-xl shadow">
                {disorder.icon}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {disorder.title}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                  {disorder.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-[#0CC8E8] to-[#2DEEAA] rounded-2xl p-8 mt-12 text-white text-center shadow-xl">
        <h2 className="text-2xl md:text-3xl font-bold mb-4">
          Early Recognition Makes a Difference
        </h2>
        <p className="text-lg md:text-xl opacity-90 max-w-2xl mx-auto mb-6">
          Recognizing these disorders early and seeking help from a speech-language pathologist can make a world of difference.
        </p>
        <div className="inline-block bg-white/20 px-6 py-3 rounded-full border border-white/20 backdrop-blur-sm text-white font-semibold">
          Stay tuned for detailed explorations
        </div>
      </div>

      {/* Footer Note */}
      <div className="mt-8 text-center text-gray-500 text-sm">
        Stay tuned as we explore each of these disorders in more detail in future posts!
      </div>
    </div>
  );
};

// Main Blog Page Component
const BlogPage = () => {
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const blogPosts = [
    {
      id: 1,
      title: "Speech Therapy Insights: 5 Key Speech and Language Disorders to Know",
      excerpt: "Communication is at the heart of how we connect, learn, and share our experiences with the world. Understanding different types of disorders is the first step...",
      category: "Speech Therapy",
      author: "Dr. Sarah Johnson",
      date: "2025-01-15",
      readTime: "8 min read",
      image: "🗣️",
      tags: ["Speech Therapy", "Language Disorders", "Communication"],
      component: <SpeechTherapyBlog />
    },
    {
      id: 2,
      title: "Understanding Autism Spectrum Disorder: Early Signs and Interventions",
      excerpt: "Early identification and intervention can make a significant difference in the lives of children with autism. Learn about the key signs to watch for...",
      category: "Autism",
      author: "Dr. Michael Chen",
      date: "2025-01-12",
      readTime: "6 min read",
      image: "🧩",
      tags: ["Autism", "Early Intervention", "Development"],
      component:<SpeechTherapyBlog/>
    },
    {
      id: 3,
      title: "The Role of Play Therapy in Child Development",
      excerpt: "Play therapy is a powerful tool for helping children express themselves and work through challenges. Discover how this therapeutic approach works...",
      category: "Play Therapy",
      author: "Dr. Emily Rodriguez",
      date: "2025-01-10",
      readTime: "5 min read",
      image: "🎲",
      tags: ["Play Therapy", "Child Development", "Therapy"],
      component:<SpeechTherapyBlog/>
    },
    {
      id: 4,
      title: "Managing Anxiety in Children: Practical Strategies for Parents",
      excerpt: "Childhood anxiety is more common than many parents realize. Learn evidence-based strategies to help your child manage anxiety and build resilience...",
      category: "Mental Health",
      author: "Dr. James Wilson",
      date: "2025-01-08",
      readTime: "7 min read",
      image: "💝",
      tags: ["Anxiety", "Mental Health", "Parenting"],
      component:<SpeechTherapyBlog/>
    },
    {
      id: 5,
      title: "Building Communication Skills Through Art Therapy",
      excerpt: "Art therapy provides a unique pathway for expression and communication. Explore how creative activities can enhance language development...",
      category: "Art Therapy",
      author: "Dr. Lisa Thompson",
      date: "2025-01-05",
      readTime: "4 min read",
      image: "🎨",
      tags: ["Art Therapy", "Communication", "Creative Expression"],
      component:<SpeechTherapyBlog/>
    },
    {
      id: 6,
      title: "Social Skills Development: Group Therapy Benefits",
      excerpt: "Group therapy sessions offer unique opportunities for children to practice social skills in a supportive environment. Learn about the benefits...",
      category: "Social Skills",
      author: "Dr. Robert Davis",
      date: "2025-01-03",
      readTime: "6 min read",
      image: "👥",
      tags: ["Social Skills", "Group Therapy", "Development"],
      component:<SpeechTherapyBlog/>
    }
  ];

  const categories = ['All', 'Speech Therapy', 'Autism', 'Play Therapy', 'Mental Health', 'Art Therapy', 'Social Skills'];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (selectedBlog) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#111920]">
        {/* Back Button */}
        <div className=" shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <button
              onClick={() => setSelectedBlog(null)}
              className="inline-flex items-center gap-2 text-white hover:text-gray-300 font-medium transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to All Blogs
            </button>
          </div>
        </div>
        
        {/* Blog Content */}
        {selectedBlog.component}
      </div>
    );
  }

  return (
    <div className="min-h-screen dark:bg-[#111920] bg-gray-50 pb-20">
      {/* Header */}
      <div className=" shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0CC8E8] to-[#2DEEAA] mb-4">
              Our Blog
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Expert insights, practical tips, and the latest research in child development, therapy, and wellness
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search Bar */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="pl-10 pr-8 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {filteredPosts.map((post) => (
            <article
              key={post.id}
              className="bg-white dark:bg-[#222a32] rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105"
              onClick={() => setSelectedBlog(post)}
            >
              {/* Image/Icon */}
              <div className="h-48 bg-gradient-to-r from-[#0CC8E8] to-[#2DEEAA] flex items-center justify-center">
                <div className="text-6xl">{post.image}</div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-[#cbd5e1] text-black px-3 py-1 rounded-full text-sm font-medium">
                    {post.category}
                  </span>
                </div>
                
                <h2 className="text-xl font-bold dark:text-gray-100 mb-3 line-clamp-2">
                  {post.title}
                </h2>
                
                <p className="dark:text-gray-100 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                
                {/* Meta Information */}
                <div className="flex items-center justify-between text-sm text-gray-400">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{post.author}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(post.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </div>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-gray-100 text-gray-700 px-2 py-1 rounded-md text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* No Results */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📝</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No articles found</h3>
            <p className="text-gray-600">Try adjusting your search terms or filter criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;